// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useContext, useEffect, useState } from 'react'
import { PropTypes } from 'prop-types'
import { Box, Button, CheckBox, FormField, TextInput } from 'grommet'
import { useTranslation } from 'react-i18next'
import lodashGet from 'lodash/get'
import { useReactOidc } from '@axa-fr/react-oidc-context'

import { WizardContext } from '../../../../../../../../components/wizard/WizardContext'
import { defaultFieldsError, StyledFormField } from '../../utils/page-utils'
import { Typography } from '../../../../../../../../components'
import { post } from '../../../../../../../../utils/api-utils'

const fetchOidcMetadata = async (
  metadataUrl,
  setFieldsError,
  setIssuerUrl,
  oidcUserReady
) => {
  try {
    if (!metadataUrl) {
      throw new Error('metadataUrl is empty.')
    }

    const url = metadataUrl.trim()
    const issuer = url.split('/.well-known/openid-configuration')[0]

    const response = await post(
      `/authn/v1/oidc/load-issuer`,
      { issuer },
      oidcUserReady.access_token
    )
    const metadata = response.data

    const requiredFields = [
      'issuer',
      'authorization_endpoint',
      'token_endpoint',
      'jwks_endpoint',
      'user_info_endpoint'
    ]

    const hasAllFields = requiredFields.every((field) => metadata[field])
    if (!hasAllFields) {
      throw new Error(
        'Unable to retrieve the endpoint information from the specified metadata URL. Check the URL and re-verify.'
      )
    }

    setIssuerUrl(metadata.issuer)
    return metadata
  } catch (error) {
    console.error('Error fetching metadata:', error.message)
    setFieldsError((prev) => ({
      ...prev,
      metadataUrl: error.message
    }))

    return null
  }
}

const ValidateMetadataURL = ({
  metadataEnabled,
  setMetadataEnabled,
  setIssuerUrl
}) => {
  const { formValues, setFormValues, attemptedAdvance } =
    useContext(WizardContext)
  const { t } = useTranslation(['authn'])

  const [isLoading, setIsLoading] = useState(false)
  const [fieldsError, setFieldsError] = useState(defaultFieldsError)
  const [isVerified, setIsVerified] = useState(false)
  const { oidcUser } = useReactOidc()

  // Track when oidcUser is ready
  const [oidcUserReady, setOidcUserReady] = useState(null)

  // Update state when oidcUser changes
  useEffect(() => {
    if (oidcUser && oidcUser.access_token) {
      setOidcUserReady(oidcUser)
    } else {
      setOidcUserReady(null)
    }
  }, [oidcUser])

  useEffect(() => {
    if (attemptedAdvance && metadataEnabled) {
      const metadataUrl = lodashGet(
        formValues,
        'oidcIdpConfig.metadata_url',
        ''
      ).trim()

      if (metadataUrl) {
        const verifyMetadata = async () => {
          const metadata = await fetchOidcMetadata(
            metadataUrl,
            setFieldsError,
            setIssuerUrl,
            oidcUserReady
          )
          if (metadata) {
            setFormValues({
              ...formValues,
              oidcIdpConfig: {
                metadata_url: metadataUrl,
                issuer: metadata.issuer,
                authorizationEndpoint: metadata.authorization_endpoint,
                tokenEndpoint: metadata.token_endpoint,
                jwksEndpoint: metadata.jwks_endpoint,
                userInfoEndpoint: metadata.user_info_endpoint
              }
            })
            setIsVerified(true)
          }
        }
        verifyMetadata()
      }
    }
  }, [
    attemptedAdvance,
    metadataEnabled,
    formValues,
    setFormValues,
    setFieldsError,
    setIssuerUrl,
    oidcUserReady
  ])

  const handleMetadataURLChange = (event) => {
    const newValue = event.target.value.trim()
    setFormValues({
      ...formValues,
      oidcIdpConfig: {
        ...formValues.oidcIdpConfig,
        metadata_url: newValue,
        issuer: '',
        authorizationEndpoint: '',
        tokenEndpoint: '',
        jwksEndpoint: '',
        userInfoEndpoint: ''
      }
    })
    setIssuerUrl('')
    setFieldsError({ ...fieldsError, metadataUrl: '' })
    setIsVerified(false)
  }

  const handleVerifyClick = async () => {
    const metadataUrl = lodashGet(
      formValues,
      'oidcIdpConfig.metadata_url',
      ''
    ).trim()
    setIsLoading(true)
    setFieldsError({ ...fieldsError, metadataUrl: '' })

    if (metadataUrl) {
      const metadata = await fetchOidcMetadata(
        metadataUrl,
        setFieldsError,
        setIssuerUrl,
        oidcUserReady
      )
      if (metadata) {
        setFormValues({
          ...formValues,
          oidcIdpConfig: {
            metadata_url: metadataUrl,
            issuer: metadata.issuer,
            authorizationEndpoint: metadata.authorization_endpoint,
            tokenEndpoint: metadata.token_endpoint,
            jwksEndpoint: metadata.jwks_endpoint,
            userInfoEndpoint: metadata.user_info_endpoint
          }
        })
        setIsVerified(true)
      } else {
        setIsVerified(false)
      }
    } else {
      setFieldsError({
        ...fieldsError,
        metadataUrl: t('authn:saml_sso.required')
      })
      setIsVerified(false)
    }
    setIsLoading(false)
  }

  return (
    <Box fill align="left" justify="start">
      <Box data-testid="metadata_url_checkbox">
        <CheckBox
          label={t(
            'iam:sso_profiles_oidc.oidc_config_with_matadata_url_checkbox'
          )}
          checked={metadataEnabled}
          onChange={(event) => {
            const isChecked = event.target.checked
            setMetadataEnabled(isChecked)
            if (!isChecked) {
              setFormValues({
                ...formValues,
                oidcIdpConfig: {
                  metadata_url: '',
                  issuer: '',
                  authorizationEndpoint: '',
                  tokenEndpoint: '',
                  jwksEndpoint: '',
                  userInfoEndpoint: ''
                }
              })
              setFieldsError(defaultFieldsError)
              setIsVerified(false)
            }
          }}
        />
      </Box>
      {metadataEnabled && (
        <Box margin={{ top: 'small' }} fill align="left" justify="start">
          <Typography
            type="text"
            size="xsmall"
            weight="500"
            testId="metadata_url_label"
          >
            {t('iam:sso_profiles_oidc.metadata_url_label')}*
          </Typography>
          <Box direction="row" align="center" gap="small">
            <FormField
              name="metadata_url_input"
              label={t('iam:sso_profiles_oidc.metadata_url_description')}
              required
              data-testid="metadata_url_description"
              error={fieldsError.metadataUrl}
            >
              <TextInput
                name="metadata_url"
                value={lodashGet(formValues, 'oidcIdpConfig.metadata_url', '')}
                onChange={handleMetadataURLChange}
                data-testid="metadata_url_input"
              />
            </FormField>
            <Box testId="validate-url-form-field">
              <StyledFormField error={fieldsError.validateUrl}>
                <Button
                  secondary
                  isLoading={isLoading}
                  label={t('common:add_sso_saml.verify_btn')}
                  data-testid="validate-url-button"
                  onClick={handleVerifyClick}
                />
              </StyledFormField>
            </Box>
          </Box>
          {isVerified && (
            <Box
              gap="small"
              background="light-2"
              pad="medium"
              margin={{ top: 'medium', bottom: 'medium' }}
              round="xsmall"
              data-testid="generated_oidc_values"
            >
              {[
                {
                  id: 1,
                  title: t('iam:sso_profiles_oidc.issuer_url_label'),
                  value: lodashGet(formValues, 'oidcIdpConfig.issuer', '')
                },
                {
                  id: 2,
                  title: t(
                    'iam:sso_profiles_oidc.authorization_endpoint_label'
                  ),
                  value: lodashGet(
                    formValues,
                    'oidcIdpConfig.authorizationEndpoint',
                    ''
                  )
                },
                {
                  id: 3,
                  title: t('iam:sso_profiles_oidc.token_endpoint_label'),
                  value: lodashGet(
                    formValues,
                    'oidcIdpConfig.tokenEndpoint',
                    ''
                  )
                },
                {
                  id: 4,
                  title: t('iam:sso_profiles_oidc.jwks_endpoint_label'),
                  value: lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint', '')
                },
                {
                  id: 5,
                  title: t('iam:sso_profiles_oidc.userinfo_endpoint'),
                  value: lodashGet(
                    formValues,
                    'oidcIdpConfig.userInfoEndpoint',
                    ''
                  )
                }
              ].map((item) => (
                <Box
                  key={item.id}
                  direction="row"
                  align="center"
                  pad={{ bottom: 'small' }}
                >
                  <Box width="30%" align="start">
                    <Typography
                      type="text"
                      size="small"
                      weight="bold"
                      data-testid={`oidc_value_title_${item.id}`}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                  <Box
                    width="70%"
                    align="start"
                    data-testid={`oidc_value_value_${item.id}`}
                  >
                    <Typography type="text" size="small">
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

ValidateMetadataURL.propTypes = {
  metadataEnabled: PropTypes.bool.isRequired,
  setMetadataEnabled: PropTypes.func.isRequired,
  setIssuerUrl: PropTypes.func.isRequired
}

export default ValidateMetadataURL
